import { useEffect, useState } from "react";
import { getUserData } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Ticket, 
  FileText, 
  CreditCard, 
  Receipt,
  ArrowRight,
  Sparkles
} from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

const ClientDashboardPortal = () => {
  const user = getUserData();
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return "Bom dia";
    if (hour < 18) return "Boa tarde";
    return "Boa noite";
  };

  const features = [
    {
      icon: Ticket,
      title: "Suporte Técnico",
      description: "Abra tickets e acompanhe suas solicitações em tempo real",
      link: "/cliente/tickets",
      available: true,
      badge: "Disponível",
      gradient: "from-primary/20 to-primary/5"
    },
    {
      icon: FileText,
      title: "Propostas",
      description: "Visualize e gerencie suas propostas comerciais",
      link: "/cliente/propostas",
      available: true,
      badge: "Disponível",
      gradient: "from-blue-500/20 to-blue-500/5"
    },
    {
      icon: CreditCard,
      title: "Faturas",
      description: "Consulte e pague suas faturas pendentes",
      link: "/cliente/faturas",
      available: true,
      badge: "Disponível",
      gradient: "from-purple-500/20 to-purple-500/5"
    },
    {
      icon: Receipt,
      title: "Contratos",
      description: "Acesse seus contratos e documentos",
      link: "/cliente/contratos",
      available: true,
      badge: "Disponível",
      gradient: "from-orange-500/20 to-orange-500/5"
    }
  ];

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border)/0.2)_1px,transparent_1px)] bg-[size:4rem_100%] pointer-events-none" />
      
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-12">
        {/* Header Section */}
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {/* Welcome Message - Left Side */}
          <div className="flex flex-col justify-center space-y-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-primary">
                <Sparkles className="h-5 w-5" />
                <span className="text-sm font-medium uppercase tracking-wider">Portal do Cliente</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
                {getGreeting()}, <br />
                <span className="text-primary">{user?.full_name?.split(' ')[0] || 'Cliente'}</span>
              </h1>
            </div>
            
            <p className="text-lg text-muted-foreground max-w-xl">
              Bem-vindo ao seu portal exclusivo. Aqui você tem acesso rápido a todos os serviços e informações importantes da sua conta.
            </p>

            <div className="flex flex-wrap gap-4 pt-4">
              <div className="flex items-center gap-2 text-sm">
                <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-muted-foreground">Sistema Online</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="h-2 w-2 rounded-full bg-primary" />
                <span className="text-muted-foreground">Suporte Ativo</span>
              </div>
            </div>
          </div>

          {/* User Info Card - Right Side */}
          <div className="flex items-center justify-center lg:justify-end">
            <Card className="w-full max-w-md bg-card/50 backdrop-blur-xl border-border/50 shadow-2xl">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="h-16 w-16 rounded-full bg-gradient-to-br from-primary to-primary/50 flex items-center justify-center text-2xl font-bold text-primary-foreground flex-shrink-0">
                    {user?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'CL'}
                  </div>
                  <div className="flex-1 space-y-3">
                    <div>
                      <h3 className="font-semibold text-lg">{user?.full_name || 'Cliente'}</h3>
                      <p className="text-sm text-muted-foreground">{user?.email || ''}</p>
                    </div>
                    
                    <div className="pt-3 border-t border-border/50 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Tipo de Conta</span>
                        <span className="font-medium">Cliente</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Status</span>
                        <span className="font-medium text-green-500">Ativo</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Último Acesso</span>
                        <span className="font-medium">
                          {currentTime.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Features Grid */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">Serviços Disponíveis</h2>
            <p className="text-sm text-muted-foreground">Acesso rápido às suas ferramentas</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              const isAvailable = feature.available;
              
              return (
                <Link
                  key={index}
                  to={isAvailable ? feature.link : '#'}
                  className={cn(
                    "group relative block",
                    !isAvailable && "cursor-not-allowed"
                  )}
                  onClick={(e) => !isAvailable && e.preventDefault()}
                >
                  <Card className={cn(
                    "h-full bg-card/30 backdrop-blur-xl border-border/50 transition-all duration-300",
                    isAvailable && "hover:bg-card/50 hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10 hover:-translate-y-1",
                    !isAvailable && "opacity-60"
                  )}>
                    <CardContent className="p-6 space-y-4">
                      {/* Icon with gradient background */}
                      <div className={cn(
                        "relative w-14 h-14 rounded-xl bg-gradient-to-br flex items-center justify-center",
                        feature.gradient
                      )}>
                        <Icon className={cn(
                          "h-7 w-7",
                          isAvailable ? "text-primary" : "text-muted-foreground"
                        )} />
                        
                        {/* Badge */}
                        <div className={cn(
                          "absolute -top-2 -right-2 px-2 py-0.5 rounded-full text-[10px] font-medium",
                          isAvailable 
                            ? "bg-primary text-primary-foreground" 
                            : "bg-muted text-muted-foreground"
                        )}>
                          {feature.badge}
                        </div>
                      </div>

                      {/* Content */}
                      <div className="space-y-2">
                        <h3 className="font-semibold text-lg flex items-center justify-between">
                          {feature.title}
                          {isAvailable && (
                            <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                          )}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {feature.description}
                        </p>
                      </div>

                      {/* Hover effect border */}
                      {isAvailable && (
                        <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-primary/0 via-primary/0 to-primary/0 group-hover:from-primary/10 group-hover:via-primary/5 group-hover:to-transparent transition-all duration-300 pointer-events-none" />
                      )}
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <Card className="bg-card/30 backdrop-blur-xl border-border/50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Tickets Abertos</p>
                  <p className="text-3xl font-bold mt-1">0</p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Ticket className="h-6 w-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/30 backdrop-blur-xl border-border/50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Propostas Ativas</p>
                  <p className="text-3xl font-bold mt-1">-</p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <FileText className="h-6 w-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/30 backdrop-blur-xl border-border/50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Faturas Pendentes</p>
                  <p className="text-3xl font-bold mt-1">-</p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <CreditCard className="h-6 w-6 text-purple-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboardPortal;
